import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Scale, Save, Loader2, Eye, RotateCcw } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const DEFAULT_DISCLAIMER = `<h2>Disclaimer</h2>
<p>Trados provides its Letter of Intent (LOI) creation and verification tools as a technology service only.</p>

<h3>No Ownership or Responsibility</h3>
<p>Trados assumes no ownership, responsibility, or liability for the content, accuracy, completeness, or legality of any LOI created using the platform. All content within any LOI document is solely the responsibility of the parties involved in its creation.</p>

<h3>No Endorsement or Guarantee</h3>
<p>Trados does not verify, endorse, or guarantee any of the information, terms, representations, or commitments contained within LOI documents created through our platform. The use of Trados branding on a document (including the "Created by Trados" badge) indicates only that the document was generated using our tools — it does not constitute an endorsement of the document's contents or the parties involved.</p>

<h3>User Responsibility</h3>
<p>Users are solely responsible for ensuring that all information provided in their LOI documents is accurate, truthful, and compliant with all applicable laws and regulations. Users should seek independent legal, financial, and professional advice before entering into any commitments based on LOI documents.</p>

<h3>Limitation of Liability</h3>
<p>To the fullest extent permitted by law, Trados shall not be held liable for any losses, damages, claims, or expenses arising from the use of or reliance on any LOI document created using our platform, including but not limited to direct, indirect, incidental, consequential, or punitive damages.</p>

<h3>Verification Services</h3>
<p>Trados LOI verification services provide automated analysis for informational purposes only. Verification results do not constitute legal or professional opinions. Users should independently verify all information and consult qualified professionals before making decisions based on verification reports.</p>

<h3>Changes to This Disclaimer</h3>
<p>Trados reserves the right to update this disclaimer at any time. Continued use of the platform constitutes acceptance of the current disclaimer terms.</p>

<p><strong>Last updated:</strong> March 2026</p>
<p>For questions, contact us at <a href="mailto:support@trados.app">support@trados.app</a></p>`;

const AdminDisclaimerSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/content/loi_disclaimer');
      if (res.data && res.data.content_value) {
        setContent(res.data.content_value);
        setLastUpdated(res.data.updated_at);
      } else {
        setContent(DEFAULT_DISCLAIMER);
      }
    } catch {
      setContent(DEFAULT_DISCLAIMER);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/content/loi_disclaimer', {
        content_value: content,
      });
      toast.success('Disclaimer updated successfully');
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save disclaimer');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULT_DISCLAIMER);
    toast.info('Reset to default — click Save to apply');
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2B4B6A] flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Legal Disclaimer</h1>
                <p className="text-sm text-gray-500">
                  Manage the disclaimer shown on LOI documents and the public disclaimer page
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Default
              </button>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2B4B6A] rounded-lg hover:bg-[#3a6590] flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>

          {lastUpdated && (
            <p className="text-xs text-gray-400 mb-4">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}

          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-800">
              This disclaimer is displayed on the public <strong>/disclaimer</strong> page
              (linked from every LOI PDF and LOI verification page). You can use HTML to format the content.
            </p>
          </div>

          {/* Editor / Preview */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {showPreview ? (
              <div
                className="p-8 prose prose-sm prose-gray max-w-none
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-800 [&_h2]:mb-4
                  [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-700 [&_h3]:mt-6 [&_h3]:mb-2
                  [&_p]:text-sm [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-3
                  [&_a]:text-[#2B4B6A] [&_a]:underline
                  [&_strong]:text-gray-700"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full p-6 font-mono text-sm text-gray-700 border-0 focus:ring-0 focus:outline-none resize-none"
                rows={30}
                placeholder="Enter HTML content for the disclaimer..."
              />
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDisclaimerSettings;
